/**
 * MarkdownContent — renders sanitized Markdown for a single chat message
 * body. Code fences delegate to `CodeBlockHighlighted` (Shiki, lazy);
 * inline code uses `MonoCodeChip` (S1 atom). Sanitization is via
 * `rehype-sanitize` so we never touch `dangerouslySetInnerHTML` on
 * untrusted input.
 *
 * Post-ADR-001 (Tailwind v4 + Radix UI): no Cloudscape imports. Element
 * styles are Tailwind utility classes that consume the Forest CSS-var
 * bridge — heading typography uses the Tiempos serif stack, body text
 * uses the Söhne sans, code uses Söhne Mono.
 *
 * This component is intentionally separate from `ChatPanel` because the
 * organism's render path streams; we do NOT want to re-run the full
 * Markdown AST per token. The streaming buffer renders as plain
 * `<span>` text; only finalized assistant messages run through Markdown.
 */

import { lazy, Suspense, useMemo } from 'react';
import type { Components } from 'react-markdown';
import type { PluggableList } from 'unified';
import remarkGfm from 'remark-gfm';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import type { Schema } from 'hast-util-sanitize';
import MonoCodeChip from '@/components/atoms/MonoCodeChip';

// Lazy-load react-markdown — its parse cost is non-trivial and the module
// is ~30 KB gz. The chat surface only needs it once a message finalizes.
const ReactMarkdown = lazy(() => import('react-markdown'));

// Lazy-load the highlighter wrapper too — keeps Shiki out of the initial
// bundle even when the chat surface is the entry route.
const CodeBlockHighlighted = lazy(() => import('./CodeBlockHighlighted'));

// rehype-sanitize default schema is conservative (no script, no event handlers).
// We extend it slightly to allow `className` on `code` so the fence language
// makes it through.
const SANITIZE_SCHEMA: Schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [
      ...((defaultSchema.attributes && defaultSchema.attributes['code']) ?? []),
      'className',
    ],
    a: [
      ...((defaultSchema.attributes && defaultSchema.attributes['a']) ?? []),
      ['target', '_blank'],
      ['rel', 'noopener', 'noreferrer'],
    ],
  },
};

const REMARK_PLUGINS: PluggableList = [remarkGfm];
const REHYPE_PLUGINS: PluggableList = [[rehypeSanitize, SANITIZE_SCHEMA]];

export interface MarkdownContentProps {
  readonly text: string;
}

export default function MarkdownContent({
  text,
}: MarkdownContentProps): React.ReactElement {
  const components = useMemo<Components>(
    () => ({
      // Map fenced code to our highlighter; inline code to the MonoCodeChip.
      code(props) {
        const { className, children } = props;
        const match = /language-(\w+)/.exec(className ?? '');
        const isBlock = !!match;
        const text = String(children ?? '').replace(/\n$/, '');
        if (isBlock) {
          const lang = match[1];
          return (
            <Suspense
              fallback={
                <pre className="font-mono text-[13px] bg-wayne-surface-2 border border-wayne-hairline rounded-lg p-4 overflow-x-auto mb-3">
                  {text}
                </pre>
              }
            >
              {lang ? (
                <CodeBlockHighlighted code={text} language={lang} />
              ) : (
                <CodeBlockHighlighted code={text} />
              )}
            </Suspense>
          );
        }
        return <MonoCodeChip compact>{text}</MonoCodeChip>;
      },
      // Force external links to open safely.
      a({ children, href, ...rest }) {
        return (
          <a
            {...rest}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-wayne-accent underline underline-offset-2 hover:text-wayne-accent-2"
          >
            {children}
          </a>
        );
      },
      h1({ children }) {
        return (
          <h1 className="font-serif text-[22px] font-medium mt-6 mb-2 tracking-tight text-wayne-ink">
            {children}
          </h1>
        );
      },
      h2({ children }) {
        return (
          <h2 className="font-serif text-[18px] font-medium mt-6 mb-2 tracking-tight text-wayne-ink">
            {children}
          </h2>
        );
      },
      h3({ children }) {
        return (
          <h3 className="text-[15px] font-medium mt-4 mb-1 text-wayne-ink">
            {children}
          </h3>
        );
      },
      p({ children }) {
        return <p className="mb-3 leading-[1.6]">{children}</p>;
      },
      ul({ children }) {
        return <ul className="mb-3 pl-6 leading-[1.6] list-disc">{children}</ul>;
      },
      ol({ children }) {
        return (
          <ol className="mb-3 pl-6 leading-[1.6] list-decimal">{children}</ol>
        );
      },
      li({ children }) {
        return <li className="mb-1">{children}</li>;
      },
      blockquote({ children }) {
        return (
          <blockquote className="border-l-2 border-wayne-hairline pl-3 my-3 text-wayne-ink-2 italic">
            {children}
          </blockquote>
        );
      },
      hr() {
        return <hr className="my-4 border-0 border-t border-wayne-hairline" />;
      },
      table({ children }) {
        return (
          <div className="overflow-x-auto mb-3">
            <table className="border-collapse border border-wayne-hairline text-[13px]">
              {children}
            </table>
          </div>
        );
      },
      th({ children }) {
        return (
          <th className="border border-wayne-hairline px-3 py-1.5 bg-wayne-surface-2 text-left font-medium">
            {children}
          </th>
        );
      },
      td({ children }) {
        return (
          <td className="border border-wayne-hairline px-3 py-1.5">
            {children}
          </td>
        );
      },
    }),
    [],
  );

  return (
    <Suspense
      fallback={<span className="whitespace-pre-wrap">{text}</span>}
    >
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={components}
      >
        {text}
      </ReactMarkdown>
    </Suspense>
  );
}
